import { Box, Button, Stack, Typography } from "@mui/material";
import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import { FileUploadButton } from "@/modules/common/components/input/file-upload/file-upload";
import { Control, Controller, SubmitHandler, useForm } from "react-hook-form";

import {
  TRACK_DOCUMENT_TYPES,
  TrackDocumentType,
} from "@/modules/property-tracks/types";
import useUploadDocument from "@/modules/property-tracks/hooks/upload-document.hook";
import { Fragment } from "react/jsx-runtime";
import { TOAST_IDS, ToastType, UploadedFile } from "@/modules/common/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { z } from "zod";
import {
  ACCEPTED_TRACK_FILE_TYPES,
  uploadDocumentSchema,
} from "@/modules/property-tracks/utils";
import { showToast } from "@/modules/common/utils";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import CommonBottomSheet from "@/modules/common/components/ui-kit/bottom-sheets/common-bottom-sheet";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import CommonModal from "@/modules/common/components/ui-kit/modals/common-modal";

type UploadDocumentFormFields = z.infer<
  ReturnType<typeof uploadDocumentSchema>
>;

const UploadForm = ({
  documentType,
  handleFiles,
  handleDeleteFile,
  fileError,
  controlUploadDocument,
}: {
  documentType: TrackDocumentType;
  handleFiles: (files: File[]) => void;
  handleDeleteFile: (file: UploadedFile) => void;
  fileError: boolean;
  controlUploadDocument: Control<any>;
}) => {
  return (
    <form>
      <Box
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <Stack
          gap={(theme) => theme.nawy.spacing.xs}
          sx={{
            minWidth: "100%",
          }}
        >
          <Typography
            sx={(theme) => ({
              ...theme.nawy.typography.english.variants.body.xs.regular,
            })}
          >
            Document
          </Typography>
          <Controller
            name="document"
            control={controlUploadDocument}
            render={() => (
              <FileUploadButton
                setFiles={handleFiles}
                acceptedFileTypes={ACCEPTED_TRACK_FILE_TYPES[documentType]}
                maxFiles={10}
                onDeleteFile={handleDeleteFile}
              />
            )}
          />
          {fileError && (
            <Typography variant="caption" color="error">
              File is required
            </Typography>
          )}
        </Stack>
      </Box>
    </form>
  );
};

const UploadDocument = ({
  documentType,
  showLabel = false,
}: {
  documentType: TrackDocumentType;
  showLabel?: boolean;
}) => {
  const { isMobile } = useScreenSize();

  const mutationUploadDocument = useUploadDocument();

  const { trackId } = useParams();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File[] | null>(null);
  const [fileError, setFileError] = useState<boolean>(false);

  const handleFiles = useCallback((newFiles: File[]) => {
    setFile(newFiles);
  }, []);

  const handleDeleteFile = useCallback((fileToDelete: UploadedFile) => {
    setFile((prevFiles) => {
      if (prevFiles === null) {
        return null;
      }
      const updatedFiles = prevFiles.filter(
        (file) => file.name !== fileToDelete.name
      );
      return updatedFiles.length > 0 ? updatedFiles : null;
    });
    setFileError(false);
  }, []);

  useEffect(() => {
    if (!file && open) {
      setFileError(true);
    }

    if (file && open) {
      setFileError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const {
    handleSubmit: handleUploadDocument,
    reset: resetUploadDocument,
    control: controlUploadDocument,
  } = useForm<UploadDocumentFormFields>({
    resolver: zodResolver(uploadDocumentSchema()),
  });

  const onUploadDocument: SubmitHandler<any> = () => {
    const formData = new FormData();

    if (file && file.length > 0) {
      file.forEach((file) => {
        formData.append("documents", file);
      });
    }

    formData.append("documentType", documentType);

    mutationUploadDocument.mutate(
      { trackId, formData },
      {
        onSuccess: () => {
          setOpen(false);
          resetFormState();
          showToast({
            type: ToastType.SUCCESS,
            message: "Document Uploaded Succcessfully",
            toastId: TOAST_IDS.UPLOAD_DOCUMENT_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message || "Error uplaoding document",
            toastId: TOAST_IDS.UPLOAD_DOCUMENT_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      }
    );
  };

  const resetFormState = () => {
    resetUploadDocument();
    setFileError(false);
    setFile(null);
  };

  const { readonly } = useReadonlyContext();

  return (
    <Fragment>
      <Button
        disabled={readonly}
        variant="text"
        onClick={() => setOpen(true)}
        sx={(t) => ({
          ...t.nawy.typography.english.variants.body.md.semibold,
          color: t.nawy.colors.semantic.text.accent,
          textTransform: "none",
        })}
      >
        {showLabel ? `Upload ${TRACK_DOCUMENT_TYPES[documentType]}` : "Upload"}
      </Button>
      {isMobile ? (
        <CommonBottomSheet
          isOpen={open}
          onClose={() => {
            resetFormState();
            setOpen(false);
          }}
          snapPoints={[0.5, 0.8]}
        >
          <ModalHeader
            onClose={() => {
              resetFormState();
              setOpen(false);
            }}
            title={`Upload ${TRACK_DOCUMENT_TYPES[documentType]}`}
          />
          <Box overflow="auto" width="100%" height="90%">
            <UploadForm
              documentType={documentType}
              handleFiles={handleFiles}
              handleDeleteFile={handleDeleteFile}
              fileError={fileError}
              controlUploadDocument={controlUploadDocument}
            />
          </Box>
          <Box position="sticky" bottom={0} width="100%">
            <ModalFooter
              onCancel={() => {
                resetFormState();
                setOpen(false);
              }}
              cancelButtonText="Cancel"
              confirmButtonText="Upload Document"
              onConfirm={handleUploadDocument(onUploadDocument)}
            />
          </Box>
        </CommonBottomSheet>
      ) : (
        <CommonModal
          open={open}
          sx={{ width: 750 }}
          onClose={() => {
            resetFormState();
            setOpen(false);
          }}
          title={`Upload ${TRACK_DOCUMENT_TYPES[documentType]}`}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              padding: "16px",
            }}
          >
            <UploadForm
              documentType={documentType}
              handleFiles={handleFiles}
              handleDeleteFile={handleDeleteFile}
              fileError={fileError}
              controlUploadDocument={controlUploadDocument}
            />
          </Box>
          <ModalFooter
            onCancel={() => {
              setOpen(false);
              resetFormState();
            }}
            cancelButtonText="Cancel"
            confirmButtonText="Upload Document"
            onConfirm={handleUploadDocument(onUploadDocument)}
            isLoading={mutationUploadDocument.isPending}
            isConfirmDisabled={!file}
          />
        </CommonModal>
      )}
    </Fragment>
  );
};

export default UploadDocument;
